import Hls from "hls.js";
import mpegts from "mpegts.js";

export type StreamKind = "hls" | "ts" | "mp4" | "unknown";

export function detectStreamKind(url: string): StreamKind {
  const lower = url.toLowerCase().split("?")[0];
  if (lower.endsWith(".m3u8")) return "hls";
  if (lower.endsWith(".ts")) return "ts";
  if (lower.endsWith(".mp4")) return "mp4";
  return "unknown";
}

export interface BuildStreamUrlOpts {
  forceTs?: boolean;     // Xtream HTTPS workaround: rewrite m3u8 → ts
  appProtocol?: "http:" | "https:";
}

export function buildStreamUrl(url: string, opts: BuildStreamUrlOpts | null): string {
  if (opts?.forceTs && url.endsWith(".m3u8")) {
    return url.replace(/\.m3u8$/, ".ts");
  }
  return url;
}

export interface PlaybackHandle {
  destroy: () => void;
  kind: StreamKind;
}

/**
 * Attach a stream to a <video> element.
 * - HLS: use hls.js if supported, else native (Safari)
 * - TS: use mpegts.js
 * - MP4: native
 * Returns a handle with a destroy() function.
 */
export function attachStream(video: HTMLVideoElement, url: string): PlaybackHandle {
  const kind = detectStreamKind(url);

  if (kind === "hls") {
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hls.loadSource(url);
      hls.attachMedia(video);
      return { destroy: () => hls.destroy(), kind };
    }
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = url;
      return { destroy: () => { video.removeAttribute("src"); }, kind };
    }
    throw new Error("HLS not supported in this browser");
  }

  if (kind === "ts") {
    if (mpegts.isSupported()) {
      const player = mpegts.createPlayer({ type: "mpegts", url, isLive: true });
      player.attachMediaElement(video);
      player.load();
      return { destroy: () => { player.destroy(); }, kind };
    }
    // Some browsers play .ts natively (Safari)
    video.src = url;
    return { destroy: () => { video.removeAttribute("src"); }, kind };
  }

  if (kind === "mp4") {
    video.src = url;
    return { destroy: () => { video.removeAttribute("src"); }, kind };
  }

  throw new Error(`Unsupported stream URL: ${url}`);
}
